import window from '../window/index.js';
import { Store } from '../hali/index.js';
import Emitter from '../common/Emitter.js';
import ArrayCacheSet from './ArrayCacheSet.js';
import ScrollLerp from './ScrollLerp.js';
import orchestrator from '../orchestrator/index.js';
import Beaker from '../Beaker/index.js';
import {
  restrictScrollPrecision,
} from '../utils/index.js';
import {
  mix,
  once,
  clamp,
  roundTo,
  uniqueId,
} from '@immutabl3/utils';
import {
  beakerPositions,
  height as calculateHeight,
  duration as calculateDuration,
  scrollPerc as calculateScrollPerc,
} from './calculate.js';
import {
  LERP_ALPHA,
  SCROLL_PERC_PRECISION,
  SCROLL_TIME_FLOAT_PRECISION,
} from '../constants.js';

const updateBeakerPositions = beakers => {
  const positions = beakerPositions(beakers);

  for (const beaker of beakers) {
    beaker.top = positions.get(beaker.id);
    beaker.last = false;
  }

  const [beaker] = beakers.slice(-1);
  beaker && (beaker.last = true);
};

const Lab = function({
  beakers: beakersConfigs,
  id = uniqueId(),
  lerp: alpha = LERP_ALPHA,
  precision = SCROLL_TIME_FLOAT_PRECISION,
} = {}) {
  const emitter = Emitter();
  const scrollLerp = ScrollLerp(); 

  const beakers = new ArrayCacheSet(
    Object.entries(beakersConfigs).map(([key, config]) => {
      return Beaker({
        key,
        ...config,
      });
    })
  );

  const store = Store({
    // here for posterity, lab is always at the top
    top: 0,
    y: 0,
    ticks: 0,
    scrollTop: 0,
    height: 0,
    perc: 0,
    time: 0,
    duration: 0,

    viewportWidth: 0,
    viewportHeight: 0,

    ...Object.fromEntries(
      beakers
        .toArray()
        .map(({ key, store }) => [key, store])
    ),
  });

  return mix(emitter, {
    get isLab() { return true; },

    get id() { return id; },
    
    get store() { return store; },
    
    init: once(() => {
      store.viewportWidth = window.width();
      store.viewportHeight = window.height();

      for (const beaker of beakers.values()) {
        beaker.init();
      }

      updateBeakerPositions(beakers.toArray());

      store.height = calculateHeight(beakers);
      store.duration = calculateDuration(beakers);

      emitter.emit('init');
    }),

    scroll(scrollTop) {
      store.ticks += 1;

      const st = scrollLerp(scrollTop, alpha);
      store.y = scrollTop;
      store.scrollTop = restrictScrollPrecision(store.scrollTop, st);

      const sp = calculateScrollPerc(store.scrollTop, store.top, store.height);
      const scrollPerc = roundTo(sp, SCROLL_PERC_PRECISION);

      store.visible = scrollPerc >= 0 && scrollPerc <= 1;

      const perc = clamp(scrollPerc, 0, 1);
      const time = roundTo(store.duration * perc, precision);

      store.perc = perc;
      store.time = time;

      for (const beaker of beakers.values()) {
        beaker.scroll(st);
      }
    },

    isIdle(scrollTop) {
      return scrollTop === scrollLerp.get();
    },

    jumpTo(scrollTop) {
      scrollLerp.set(scrollTop);
    },

    resize() {
      for (const beaker of beakers.values()) {
        beaker.resize();
      }

      updateBeakerPositions(beakers.toArray());

      store.height = calculateHeight(beakers);
      store.duration = calculateDuration(beakers);
      
      store.viewportWidth = window.width();
      store.viewportHeight = window.height();

      return store.height;
    },
  });
};

const instances = new Map();
export default function LabFactory(config) {
  if (instances.has(config)) return instances.get(config);
  
  const lab = Lab(config);

  orchestrator.add(lab);

  lab.on('unmount', () => orchestrator.remove(lab));
  lab.on('init', () => orchestrator.reflow());

  instances.set(config, lab);

  return lab;
};